import { BodyContainer, Cell, Footer, Header, Row, Table } from 'wed-components-beta'
import { Data } from './AccountBalance'
import { formatNumberToMoney } from '../../../Utils'
import { DateTime } from 'luxon'
import { IconButton } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Fragment, useState } from 'react'

type Props = {
  data: Data
  months: DateTime[]
}

export default function AccountBalanceTable({ data, months }: Props) {
  const columnWidth = [.5, 3, 1.5, 1.25, 1.25]
  // General
  const [expandedRows, setExpandedRows] = useState<number[]>([])

  const onScrollLeft = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    document.getElementById('body-container-right')?.scrollTo({ top: e.currentTarget.scrollTop })
  }

  const onScrollRight = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    document.getElementById('body-container-left')?.scrollTo({ top: e.currentTarget.scrollTop })
  }

  return <Table>
    <BodyContainer
      id="body-container-left"
      flexShrink={0}
      onScroll={onScrollLeft}
    >
      <Header>
        <Cell width={columnWidth[0]}>
          ID
        </Cell>
        <Cell width={columnWidth[1]}>
          Affiliation
        </Cell>
        <Cell width={columnWidth[2]}>
          Year
        </Cell>
        <Cell width={columnWidth[3]}>
          Spring
        </Cell>
        <Cell width={columnWidth[4]}>
          Fall
        </Cell>
      </Header>
      {
        data.groupedAccountBalance.map((affiliation, index) => <Fragment key={index}>
          <Row>
            <Cell width={columnWidth[0]}>
              {affiliation.id}
            </Cell>
            <Cell width={columnWidth[1]} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              {affiliation.affiliation_name}
              <IconButton
                id={`button-expand-${affiliation.id}`}
                onClick={() => setExpandedRows((rows) => rows.includes(affiliation.id) ? rows.filter((_) => _ !== affiliation.id) : rows.concat(affiliation.id))}
                sx={{ width: 24, height: 24 }}
              >
                {expandedRows.includes(affiliation.id) ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Cell>
            <Cell width={columnWidth[2]}>
              {affiliation.total.map((_, index) => <p key={index}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)}
            </Cell>
            <Cell width={columnWidth[3]}>
              {affiliation.spring_total.map((_, index) => <p key={index}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)}
            </Cell>
            <Cell width={columnWidth[4]}>
              {affiliation.fall_total.map((_, index) => <p key={index}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)}
            </Cell>
          </Row>
          {
            // Per grade
            expandedRows.includes(affiliation.id) ?
              data.perGradeTotal.filter((_) => _.fk_id_affiliation === affiliation.id).map((grade, gradeIndex) => <Row key={gradeIndex}>
                <Cell>
                  {grade.grade_name}
                </Cell>
                <Cell flexGrow>
                  {grade.currency_sign} {formatNumberToMoney(grade.due_value)}
                </Cell>
              </Row>)
              :
              null
          }
        </Fragment>)
      }
      {/* Totals */}
      <Footer>
        <Cell flexGrow>
          Total
        </Cell>
        <Cell width={columnWidth[2]}>
          {
            data.yearTotal.map((_, index) => <p key={index}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)
          }
        </Cell>
        <Cell width={columnWidth[3]}>
          {
            data.springTotal.map((_, index) => <p key={index}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)
          }
        </Cell>
        <Cell width={columnWidth[4]}>
          {
            data.fallTotal.map((_, index) => <p key={index}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)
          }
        </Cell>
      </Footer>
    </BodyContainer>
    <BodyContainer
      id="body-container-right"
      allowCellOverflow
      onScroll={onScrollRight}
    >
      <Header>
        {
          months.map((month, index) => <Cell key={index}>{month.toFormat('MMM yy')}</Cell>)
        }
      </Header>
      {/* Monthly per affiliation */}
      {
        data.groupedAccountBalance.map((affiliation, rowIndex) => <Fragment key={rowIndex}>
          <Row>
            {
              months.map((month, columnIndex) => <Cell key={columnIndex}>
                {
                  affiliation.balance
                    .filter((_) => _.month === month.get('month'))
                    .map((_, valueIndex) => <p key={valueIndex}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)
                }
              </Cell>)
            }
          </Row>
          {
            expandedRows.includes(affiliation.id) ?
              data.perGradeTotal.filter((_) => _.fk_id_affiliation === affiliation.id).map((_) => _.fk_id_grade).map((grade, rowIndex) => <Row key={rowIndex}>
                {
                  months.map((month, columnIndex) => <Cell key={columnIndex} style={{ alignItems: 'flex-end' }}>
                    {
                      affiliation.per_grade
                        .filter((_) => _.month === month.get('month') && _.fk_id_grade === grade)
                        .map((_, valueIndex) => <p key={valueIndex}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)
                    }
                  </Cell>)
                }
              </Row>)
              :
              null
          }
        </Fragment>)
      }
      {/* Monthly total */}
      <Footer>
        {
          months.map((month, index) => {
            return <Cell key={index}>
              {
                data.monthlyTotal
                  .filter((_) => _.month === month.get('month'))
                  .map((_, valueIndex) => <p key={valueIndex}>{_.currency_sign} {formatNumberToMoney(_.due_value)}</p>)
              }
            </Cell>
          })
        }
      </Footer>
    </BodyContainer >
  </Table >
}